<template>
  <!--
    App-Rahmen im Jira-Stil für alle Bereiche:
    Top-Navigation 48 px · Seitenleiste 240 px (Bereiche und Unterseiten) · Inhalt.
    Navigation, Benutzermenü und Logout stecken hier – Seiten liefern nur ihren Inhalt.
  -->
  <div class="ads jr-calm" lang="de">
    <div class="jr-app" :class="{ 'jr-app--nav-open': navOpen }">
      <header class="jr-top">
        <button type="button" class="jr-icon-btn jr-top__toggle" aria-label="Seitenleiste umschalten" @click="navOpen = !navOpen">
          <i class="fas fa-bars" aria-hidden="true" />
        </button>
        <router-link :to="homeRoute" class="jr-top__brand">
          <span class="jr-top__mark" aria-hidden="true"><i class="fas fa-gauge-high" /></span>
          <span class="jr-top__product">Steuerungs<span class="jr-top__product-light">cockpit</span></span>
        </router-link>
        <div class="jr-top__spring" />
        <UserMenu :user-name="access.username" @logout="logout" />
      </header>

      <div v-if="navOpen" class="jr-nav-back" @click="navOpen = false" />
      <nav class="jr-side" aria-label="Bereiche">
        <p class="jr-side__heading">Bereiche</p>
        <template v-for="area in areas" :key="area.key">
          <router-link
            :to="area.to"
            class="jr-side__item jr-side__item--group"
            :class="{ 'is-selected': area.selected && !area.children, 'is-active': area.active }"
            :style="{ '--area': area.color }"
            :aria-current="area.selected && !area.children ? 'page' : undefined"
          >
            <span class="jr-side__space" aria-hidden="true">
              <i :class="['fas', area.icon]" />
            </span>
            {{ area.label }}
          </router-link>
          <div v-if="area.summary && area.active" class="jr-side__children">
            <!-- Übersichtsseite als Oberpunkt, die Einzelseiten hängen darunter (Baum). -->
            <router-link
              :to="area.summary.to"
              class="jr-side__item jr-side__item--summary"
              :class="{ 'is-selected': area.summary.selected }"
              :aria-current="area.summary.selected ? 'page' : undefined"
            >
              <i :class="['fas', area.summary.icon, 'jr-side__icon']" aria-hidden="true" />{{ area.summary.label }}
            </router-link>
            <div class="jr-side__tree">
              <router-link
                v-for="item in area.children"
                :key="item.name"
                :to="item.to"
                class="jr-side__item jr-side__item--leaf"
                :class="{ 'is-selected': item.selected }"
                :aria-current="item.selected ? 'page' : undefined"
              >
                <i :class="['fas', item.icon, 'jr-side__icon']" :style="{ color: item.color }" aria-hidden="true" />{{ item.label }}
              </router-link>
            </div>
          </div>
        </template>
      </nav>

      <main class="jr-main">
        <slot />
      </main>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';
import { getApiBaseUrl } from '@/config';
import UserMenu from './UserMenu.vue';
import { CHANNEL_COLORS } from '@/features/marketing/composables/designTokens';
import { useAccess } from '@/composables/useAccess';
import { MARKETING_FILTERS_KEY } from '@/features/marketing/composables/useMarketingFilters';
import '@/assets/styles/ads.css';

const route = useRoute();
const router = useRouter();
const navOpen = ref(false);
const { access, loaded, canAccessFinancial, canAccessMarketing, canAccessDatamanagement, loadAccess, resetAccess } = useAccess();

// Innerhalb von Marketing übernehmen die Unterseiten-Links die aktiven Filter.
const marketingFilters = inject(MARKETING_FILTERS_KEY, null);

// Kampagnenübersicht fasst die Kanalseiten zusammen (Seitenleiste: Oberpunkt mit Baum darunter).
const MARKETING_OVERVIEW = { name: 'marketing-overview', label: 'Kampagnenübersicht', icon: 'fa-table-cells-large' };
const MARKETING_PAGES = [
  { name: 'marketing-digital', label: 'Digitale Kampagnen', icon: 'fa-display', color: CHANNEL_COLORS.digital },
  { name: 'marketing-social', label: 'Social Media', icon: 'fa-share-nodes', color: CHANNEL_COLORS.social },
  { name: 'marketing-email', label: 'E-Mail & Newsletter', icon: 'fa-envelope', color: CHANNEL_COLORS.email },
  { name: 'marketing-traffic', label: 'Website-Traffic', icon: 'fa-arrow-trend-up', color: CHANNEL_COLORS.traffic },
  { name: 'marketing-print', label: 'Print-Kampagnen', icon: 'fa-newspaper', color: CHANNEL_COLORS.print }
];

function marketingLink(page) {
  return {
    ...page,
    selected: route.name === page.name,
    to: { name: page.name, query: marketingFilters?.queryFor(page.name) }
  };
}

const currentArea = computed(() => {
  if (route.name === 'datamanagementarea') return 'data';
  if (String(route.name || '').startsWith('marketing')) return 'marketing';
  return 'financial';
});

const areas = computed(() => {
  const list = [];
  if (canAccessFinancial.value) {
    list.push({
      key: 'financial', label: 'Finanzen', icon: 'fa-chart-column', color: '#23799D',
      to: { name: 'dashboard' }, active: currentArea.value === 'financial', selected: currentArea.value === 'financial'
    });
  }
  if (canAccessMarketing.value) {
    list.push({
      key: 'marketing', label: 'Marketing', icon: 'fa-bullhorn', color: '#7A4BA6',
      to: { name: 'marketing-overview', query: marketingFilters?.queryFor('marketing-overview') },
      active: currentArea.value === 'marketing',
      selected: currentArea.value === 'marketing',
      summary: marketingLink(MARKETING_OVERVIEW),
      children: MARKETING_PAGES.map(marketingLink)
    });
  }
  if (canAccessDatamanagement.value) {
    list.push({
      key: 'data', label: 'Datenmanagement', icon: 'fa-database', color: '#6B6E76',
      to: { name: 'datamanagementarea', query: { user: access.value.username } },
      active: currentArea.value === 'data', selected: currentArea.value === 'data'
    });
  }
  return list;
});

const homeRoute = computed(() => areas.value[0]?.to || '/');

async function logout() {
  try {
    await axios.post(`${getApiBaseUrl()}/logout/`);
  } finally {
    resetAccess();
    router.push('/');
  }
}

onMounted(() => {
  if (!loaded.value) loadAccess().catch(() => {});
});

// Mobil schließt die Seitenleiste nach der Navigation.
watch(() => route.fullPath, () => {
  navOpen.value = false;
});
</script>
